import { TEventBus } from '@eventBus/EventBus';
import { Logger, TLogger } from '@logger/Logger';
import { FragmentLoaderEvents } from '@streamLoader/FragmentLoader/FragmentLoader.events';
import { isValidURL } from '@utils/URL/isValidURL';
import bind from '@utils/decorators/bind';

import { TFragmentInitData } from './IFragment';

const DURATION_TO_CONSIDER_FRAGMENT_STUCK: number = 3000;

export class Fragment {
  public url: string;
  public duration: TFragmentInitData['duration'];
  public msn: TFragmentInitData['msn'];
  public pn: TFragmentInitData['pn'];
  public isInitSection: TFragmentInitData['isInitSection'];

  public isTransmuxingBypassRequested?: TFragmentInitData['isTransmuxingBypassRequested'];
  public independent?: TFragmentInitData['independent'];
  public startTs?: TFragmentInitData['startTs'];
  public endTs?: TFragmentInitData['endTs'];
  public data?: TFragmentInitData['data'];
  public requestedLoadingTimeout?: TFragmentInitData['requestedLoadingTimeout'];
  public stats?: TFragmentInitData['stats'];
  public error?: { status: number; statusText: string };

  private readonly _logger: TLogger | null = null;
  private _shouldLogHistory: boolean = false;
  private _eventBus?: TEventBus;
  private _history: Array<{ event: string; timeSincePreviousEvent: number }> = [];
  private _hasLifecycleEnded: boolean = false;
  private _isStuckTimer: ReturnType<typeof setTimeout> | null = null;
  private _lastTs: number = Date.now();

  constructor(meta: TFragmentInitData, eventBus?: TEventBus, shouldLogHistory?: boolean) {
    Object.assign(this, meta);
    this._eventBus = eventBus;
    this._shouldLogHistory = shouldLogHistory || false;
    if (this._eventBus) {
      this._logger = Logger(this._eventBus, 'Doppio - Fragment');
    }

    // we assign these three fields manually only to satisfy typescript. in runtime that doesn't make any sense
    this.duration = meta.duration;
    this.msn = meta.msn;
    this.pn = meta.pn;

    if (meta.playlistUrl || meta.fragmentUrl) {
      this.url = Fragment._buildUrl(meta.playlistUrl, meta.fragmentUrl);
    } else {
      this.url = meta.url as string;
    }

    this._logger?.verbose(`Fragment ${this.getMsnPn()} created`);
    this.goToLifecycleStage('created');
  }

  public getMsnPn(): string {
    return `${this.msn}/${this.pn || 0}`;
  }

  public goToLifecycleStage(
    newLifecycleStage:
      | 'created'
      | 'loadDataStart'
      | 'loadDataEnd'
      | 'loadDataAborted'
      | 'addedToQueue'
      | 'releasedFromQueue'
      | 'addedToTransmuxer'
      | 'releasedFromTransmuxer'
      | 'addedToBuffer',
  ): void {
    this._clearStuckTimer();

    if (!this._shouldLogHistory || this._hasLifecycleEnded) {
      return;
    }

    const newTs: number = Date.now();
    this._history.push({ event: newLifecycleStage, timeSincePreviousEvent: newTs - this._lastTs });
    this._lastTs = newTs;

    this._logger?.debug(`Fragment ${this.getMsnPn()} lifecycle updated: ${newLifecycleStage}`);

    if (newLifecycleStage === 'loadDataAborted') {
      this._hasLifecycleEnded = true;
      this._publishHistoryReadyForPrint('Fragments lifecycle: ABORTED');
    } else if (newLifecycleStage === 'addedToBuffer') {
      this._hasLifecycleEnded = true;
      this._publishHistoryReadyForPrint('Fragments lifecycle: SUCCESS');
    } else {
      this._isStuckTimer = setTimeout(this._handleFragmentStuck, DURATION_TO_CONSIDER_FRAGMENT_STUCK);
    }
  }

  private static _buildUrl(playlistUrl: string, fragmentUrl: string): string {
    if (isValidURL(fragmentUrl)) {
      return fragmentUrl;
    }

    const parsedPlaylistUrl: URL = new URL(playlistUrl);

    if (fragmentUrl.startsWith('/')) {
      return `${parsedPlaylistUrl.origin}${fragmentUrl}`;
    }

    const pathParts: string[] = parsedPlaylistUrl.pathname.split('/');
    // Removes last path part (or two if last part is empty and URL has a trailing slash
    pathParts.pop() || pathParts.pop();

    return `${parsedPlaylistUrl.origin}/${pathParts.concat(fragmentUrl).join('/')}`;
  }

  @bind
  private _handleFragmentStuck(): void {
    this._publishHistoryReadyForPrint(`Fragments lifecycle: STUCK`);
  }

  private _publishHistoryReadyForPrint(title: string): void {
    this._eventBus?.publish(FragmentLoaderEvents.fragments.fragmentHistoryReadyForPrint, {
      historyString: `${title} ${this.getMsnPn()}:\n${this._history
        .map((record) => `Event: ${record.event}, ${record.timeSincePreviousEvent}ms since previous event`)
        .join('\n')}`,
    });
  }

  private _clearStuckTimer(): void {
    if (this._isStuckTimer) {
      clearTimeout(this._isStuckTimer);
    }
  }
}
